import { type Schema } from 'normalizr';
import qs from 'query-string';
import { type Observable } from 'rxjs';
import { type AjaxConfig } from 'rxjs/ajax';

import { type UsageResponse } from '../accounts/api';
import { getDefaultDateRangeForHighUsage, type SDKType } from '../accounts/model';
import config from '../config';
import { request as baseRequest } from '../shared';

const base = config.gonfalon.host;
const ghostBase = config.gonfalon.ghostUrl;

export const ghostingEnabled = !!ghostBase;
export const url = config.gonfalon.publicUrl;

export const request = <T>(
  urlOrRequest: string | AjaxConfig,
  schema?: Schema,
  timeoutOverride?: number,
): Observable<T> => {
  let req: AjaxConfig;

  const baseHeaders = {
    'Content-Type': 'application/json',
    ...(config.gonfalon.localPrivateAuth && { 'X-LD-Private': 'allowed' }),
  };

  const baseOptions = {
    // 24 seconds is the timeout Gonfalon hardcodes (API gateway timeout is 25s)
    timeout: timeoutOverride || 24000,
    crossDomain: true,
    withCredentials: true,
  };

  if (typeof urlOrRequest === 'string') {
    req = {
      ...baseOptions,
      url: `${base}${urlOrRequest}`,
      headers: baseHeaders,
    };
  } else {
    req = {
      ...urlOrRequest,
      ...baseOptions,
      url: `${base}${urlOrRequest.url}`,
      headers: {
        ...(urlOrRequest.headers as object),
        ...baseHeaders,
      },
    };
  }

  return baseRequest(req, schema);
};

export const requestGhostingCookie = (accountId: string, email: string): Promise<Response | void> => {
  if (!ghostBase) {
    return Promise.reject(new Error('Can not request ghosting cookie without ghostBase'));
  }
  const encodedEmail = encodeURIComponent(email);
  return fetch(`${ghostBase}/private/ghost/${encodedEmail}/${accountId}`, {
    credentials: 'include',
    ...(config.gonfalon.localPrivateAuth && { headers: { 'X-LD-Private': 'allowed' } }),
  });
};

export const getHighAccountUsage = (usageType: SDKType, dates?: { to: string; from: string }) =>
  request<UsageResponse>({
    url: `/private/high-usage/stream/${usageType}?${qs.stringify(dates || getDefaultDateRangeForHighUsage())}`,
  });
